import React from 'react';
import Button from './Button';
import Icon from './Icon';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Catch errors in any components below and re-render with error message
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log error to console for debugging
    console.error('Error Boundary caught an error:', error, errorInfo);

    // You can also log the error to an error reporting service here
    // logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          background: 'var(--bg)',
          color: 'var(--text)'
        }}>
          <div style={{
            maxWidth: '500px',
            textAlign: 'center',
            background: 'var(--bg-deep)',
            padding: '40px',
            borderRadius: '12px',
            border: '1px solid var(--border)'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: 'var(--red-light)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <Icon name="warn" size={32} color="var(--red)" />
            </div>
            
            <h1 style={{
              fontSize: '24px',
              fontWeight: '600',
              marginBottom: '12px',
              color: 'var(--text)'
            }}>
              Something went wrong
            </h1>
            
            <p style={{
              fontSize: '16px',
              color: 'var(--text-muted)',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{
                textAlign: 'left',
                background: 'var(--bg)',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '24px',
                fontSize: '12px',
                color: 'var(--text-muted)'
              }}>
                <summary style={{ 
                  cursor: 'pointer', 
                  fontWeight: '600',
                  marginBottom: '8px'
                }}>
                  Error Details (Development Only)
                </summary>
                <pre style={{ 
                  whiteSpace: 'pre-wrap', 
                  wordBreak: 'break-word',
                  margin: 0,
                  fontSize: '11px'
                }}>
                  {this.state.error && this.state.error.toString()}
                  <br />
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <Button
                onClick={this.handleReset}
                style={{
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Try Again
              </Button>
              
              <Button
                variant="secondary"
                onClick={() => window.location.reload()}
                style={{
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Refresh Page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
